import { Injectable, inject } from '@angular/core';
import { AITool } from '../tool.decorator';
import { AccountOfflineService } from '@omni/services/account/account.offline.service';
import { ContactOfflineService } from '@omni/services/contact/contact.service';
import { ActivityService } from '@omni/services/activity/activity.service';
import { AppointmentActivity } from '@omni/classes/activity/appointment.activity.class';

/**
 * Aggregate "what's in the box" reads — counts and next-up — across the
 * account / contact / activity caches. Same offline-first source as the
 * search tools, but rolled up rather than filtered.
 */
@Injectable({ providedIn: 'root' })
export class DataSummaryTools {
  private readonly accountService = inject(AccountOfflineService);
  private readonly contactService = inject(ContactOfflineService);
  private readonly activityService = inject(ActivityService);

  @AITool('get_data_statistics', 'Get summary statistics about accounts, contacts, and activities')
  async getDataStatistics(): Promise<any> {
    try {
      const accounts = this.accountService.accounts || [];
      const contacts = this.contactService.contacts || [];
      const activities = this.activityService.activities;

      const accountCount = accounts.length;
      const contactCount = contacts.length;
      const activityCount = Array.isArray(activities) ? activities.length : 0;

      return {
        success: true,
        statistics: {
          accounts: accountCount,
          contacts: contactCount,
          activities: activityCount,
        },
      };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to get statistics' };
    }
  }

  @AITool('get_next_meeting', "Get the user's next upcoming meeting")
  async getNextMeeting(): Promise<any> {
    try {
      const results = this.activityService.activities;
      const activities = Array.isArray(results) ? results : [];
      const nextMeeting = activities.find(a =>
        a instanceof AppointmentActivity &&
        a.scheduledStart &&
        new Date(a.scheduledStart) > new Date(),
      );

      if (!nextMeeting) {
        return { success: true, message: 'No upcoming meetings found' };
      }

      const appointment = nextMeeting as AppointmentActivity;
      return {
        success: true,
        meeting: {
          id: appointment.ID,
          subject: appointment.subject,
          date: appointment.scheduledStart,
          location: appointment.location,
          contacts: (appointment as any).contacts?.map((c: any) => c.fullName || c.name) || [],
        },
      };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to get meetings' };
    }
  }
}
